import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { galleryItems } from "@/config/business";
import { Reveal, Overline } from "@/components/Reveal";

const categories = ["All", "Hair", "Makeup", "Beauty", "Nails", "Salon"];

export default function Gallery() {
  const [filter, setFilter] = useState("All");
  const [lightbox, setLightbox] = useState(null);
  const items = filter === "All" ? galleryItems : galleryItems.filter((g) => g.category === filter);

  const close = useCallback(() => setLightbox(null), []);
  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") setLightbox((i) => (i + 1) % items.length);
      if (e.key === "ArrowLeft") setLightbox((i) => (i - 1 + items.length) % items.length);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox, items.length, close]);

  return (
    <section id="gallery" className="py-20 md:py-32 bg-cream" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <Reveal>
          <Overline>Gallery</Overline>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight text-ink max-w-xl">
            Moments from the Studio
          </h2>
          <p className="mt-4 text-sm text-muted-foreground">
            Demo placeholder photography — to be replaced with the salon's own photos before launch.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-wrap gap-2" role="tablist" aria-label="Gallery categories">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`gallery-filter-${c.toLowerCase()}`}
                role="tab"
                aria-selected={filter === c}
                onClick={() => setFilter(c)}
                className={`px-5 py-2.5 text-sm font-medium tracking-wide border transition-colors min-h-[44px] ${
                  filter === c
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-primary/20 text-ink/70 hover:border-primary hover:text-primary"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-10 columns-2 md:columns-3 gap-4 [column-fill:_balance]">
          {items.map((g, i) => (
            <motion.button
              key={g.src + g.category}
              data-testid={`gallery-item-${i}`}
              layout
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              onClick={() => setLightbox(i)}
              className="mb-4 block w-full overflow-hidden break-inside-avoid group"
              aria-label={`Open image: ${g.alt}`}
            >
              <img
                src={g.src}
                alt={g.alt}
                loading="lazy"
                className="w-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {lightbox !== null && items[lightbox] && (
          <motion.div
            data-testid="gallery-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label="Image viewer"
            className="fixed inset-0 z-[80] bg-deep/95 flex items-center justify-center p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
          >
            <button
              data-testid="lightbox-close"
              onClick={close}
              aria-label="Close image viewer"
              className="absolute top-5 right-5 text-cream p-3 hover:text-accent transition-colors"
            >
              <X size={28} />
            </button>
            <button
              data-testid="lightbox-prev"
              aria-label="Previous image"
              onClick={(e) => {
                e.stopPropagation();
                setLightbox((i) => (i - 1 + items.length) % items.length);
              }}
              className="absolute left-3 md:left-8 text-cream p-3 hover:text-accent transition-colors"
            >
              <ChevronLeft size={32} />
            </button>
            <motion.img
              key={items[lightbox].src}
              src={items[lightbox].src}
              alt={items[lightbox].alt}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35 }}
              className="max-h-[80vh] max-w-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              data-testid="lightbox-next"
              aria-label="Next image"
              onClick={(e) => {
                e.stopPropagation();
                setLightbox((i) => (i + 1) % items.length);
              }}
              className="absolute right-3 md:right-8 text-cream p-3 hover:text-accent transition-colors"
            >
              <ChevronRight size={32} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
